import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Box, Tab, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ColoredDot from '@pages/report/Lighthouse/components/ColoredDot.jsx';
import PropTypes from 'prop-types';
import { useState, useMemo } from 'react';

/**
 * Component to display tabs with panels.
 *
 * @param {Object} props - The properties object.
 * @param {string} [props.tabTextColor='#000'] - The color of the text for the tabs.
 * @param {Array<Object>} [props.tabs=[]] - Array of tab objects, each representing a tab.
 * @param {number} [props.selectedTab=0] - The index of the initially selected tab.
 * @param {Object} [props.tabPanelProps={}] - Additional props to pass to the TabPanel component.
 * @param {Object} [props.tabListProps={}] - Additional props to pass to the TabList component.
 * @param {Function} [props.handleTabChange] - Callback function triggered when the active tab changes.
 * @param {Function} [props.TabPanelRenderer=DefaultTabPanelRenderer] - Custom renderer for the TabPanel component.
 *
 * @typedef {Object} Tab - Represents a single tab.
 * @property {string} [key] - Unique key for the tab.
 * @property {string} title - The title of the tab.
 * @property {React.Element|string} content - The content to display in the tab panel.
 * @property {Function} [onClick] - Callback function triggered when the tab is clicked.
 * @property {boolean|number} [showDot] - Whether to show a colored dot next to the tab title.
 * @property {string} [description] - Optional description to display in the tab panel.
 *
 * @returns {React.Element} The rendered TabsWithPanels component.
 */
function TabsWithPanels({
    tabTextColor = '#000',
    tabs = [],
    selectedTab = 0,
    tabPanelProps = {},
    tabListProps = {},
    handleTabChange,
    TabPanelRenderer = DefaultTabPanelRenderer,
}) {
    const [activeTab, setActiveTab] = useState(selectedTab);

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
    const variant = isSmallScreen ? 'scrollable' : 'fullWidth';

    function handleChange(e, val) {
        setActiveTab(val);
        if (handleTabChange) {
            handleTabChange();
        }
    }

    const visibleTabs = useMemo(() => tabs.filter(tab => !tab.hidden), [tabs]);

    return (
        <TabContext value={activeTab}>
            <TabList
                scrollButtons
                allowScrollButtonsMobile
                selectionFollowsFocus
                onChange={handleChange}
                aria-label='Subnavigation Tabs'
                variant={variant}
                {...tabListProps}
            >
                {visibleTabs.map((tab, idx) => {
                    const { content, showDot, title, ...rest } = tab; // eslint-disable-line
                    const label = showDot ? (
                        <Box
                            sx={{
                                alignItems: 'center',
                                display: 'flex',
                                color: 'inherit',
                            }}
                            key={tab.key || title + idx}
                        >
                            <Box
                                sx={{
                                    fontWeight: 600,
                                    color: 'inherit',
                                }}
                            >
                                {title}
                            </Box>
                            <Box
                                sx={{
                                    width: '25px',
                                }}
                            >
                                <ColoredDot color='#e80000' />
                            </Box>
                        </Box>
                    ) : (
                        title
                    );
                    return (
                        <Tab
                            {...rest}
                            disableRipple
                            disableFocusRipple
                            sx={{
                                'color': tabTextColor,
                                ':focus, :hover': {
                                    outline: `3px solid ${tabTextColor} !important`,
                                    outlineOffset: '-3px',
                                },
                                ...(rest.sx || {}),
                            }}
                            key={tab.title + idx}
                            label={label}
                            value={idx}
                        />
                    );
                })}
            </TabList>
            {visibleTabs.map((tab, idx) => {
                return (
                    <TabPanelRenderer
                        tab={tab}
                        activeTabIndex={idx}
                        key={tab.key || tab.title}
                        {...tabPanelProps}
                    />
                );
            })}
        </TabContext>
    );
}

const DefaultTabPanelRenderer = ({ tab, activeTabIndex, ...props }) => {
    return (
        <TabPanel value={activeTabIndex} {...props}>
            {tab.description || null}
            {tab.content}
        </TabPanel>
    );
};

TabsWithPanels.propTypes = {
    tabTextColor: PropTypes.string,
    tabs: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string,
            title: PropTypes.string.isRequired,
            content: PropTypes.oneOfType([PropTypes.element, PropTypes.string]).isRequired,
            onClick: PropTypes.func,
            showDot: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
            description: PropTypes.string,
        })
    ).isRequired,
};

export default TabsWithPanels;
